import { observer } from "mobx-react";
import { useMemo } from "react";
import { useMobxStores } from "../../stores/stores";
import { Row, message, Tooltip, Modal } from "antd";
import { keycloak } from "../../utils/keycloak";
import { isMoocLink, isRepositoryLink } from "../utils/urlValidators";
import { handleDownload } from "../../utils/itemButtonActions";
import "./ItemDetailsActionButton.css";
import { useParams } from "react-router-dom";
import { isMobile } from "react-device-detect";

const ItemDetailsActionButton = ({ setModalVisible }) => {
  const { marketplaceStore } = useMobxStores();
  const params = useParams();

  const requireAuth = (onAuthenticated) => {
    if (keycloak.authenticated) {
      onAuthenticated();
    } else {
      Modal.confirm({
        title: "Sign in required",
        content:
          "You are not allowed to perform this action. You need to sign in first.",
        okText: "Sign in",
        cancelText: "Cancel",
        onOk: () => keycloak.login({ redirectUri: window.location.href }),
      });
    }
  };

  const handleSimpleBuyNFT = async () => {
    marketplaceStore.setBuyInProgress(true);
    const metamaskData = localStorage.getItem("metaMask");
    if (!metamaskData) {
      message.error("Please connect to Metamask first");
      return;
    }

    try {
      await marketplaceStore.GetListingsById(
        marketplaceStore.marketplaceItem.item.nft,
      );
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [
          {
            chainId: `0x${marketplaceStore.marketplaceItem.item.chainid.toString(
              16,
            )}`,
          },
        ],
      });
      let values = {
        account: metamaskData,
        listing: marketplaceStore.listings[0].id,
        quantity: 1,
      };

      await marketplaceStore.BuyNFT(
        marketplaceStore.marketplaceItem.item.nft,
        values,
      );

      await marketplaceStore.UpdateNFT(marketplaceStore.NFT?.id, {
        listed: false,
      });
      marketplaceStore.GetMarketplaceItemById(params.pk);
    } catch (error) {
      console.error(error);
    } finally {
      marketplaceStore.setBuyInProgress(false);
    }
  };

  const buttonContent = useMemo(() => {
    if (marketplaceStore.marketplaceItem?.item.nft) {
      if (marketplaceStore.NFT?.supply > 1) return "Purchase options";
      if (marketplaceStore.NFT?.supply === 1) return "Buy Now";
    } else {
      if (
        marketplaceStore.marketplaceItem?.item.type === "software" &&
        isRepositoryLink(marketplaceStore.marketplaceItem?.item.source_media)
      ) {
        return "Repository";
      }
      if (
        marketplaceStore.marketplaceItem?.item.type === "MOOC" &&
        isMoocLink(marketplaceStore.marketplaceItem?.item.source_media)
      ) {
        return "MOOC";
      }
      return "Download";
    }
  }, [marketplaceStore.NFT, marketplaceStore.marketplaceItem]);

  const renderActionButton = () => {
    if (marketplaceStore.NFT?.supply > 1) {
      return (
        <Row justify="center">
          {marketplaceStore.NFT?.listed &&
          marketplaceStore.metaMaskIsConnected ? (
            <button
              className=" action-button action-button-nft-listed"
              onClick={() =>
                requireAuth(() => {
                  if (
                    marketplaceStore.marketplaceItem?.item.nft &&
                    marketplaceStore.NFT?.supply > 1
                  ) {
                    setModalVisible("purchaseOptions");
                  }
                })
              }
            >
              {buttonContent}
            </button>
          ) : (
            <Tooltip
              title="Buying is currently unavailable. The owner may have disabled it, or your wallet is not connected."
              backgroundColor="#00000000"
              trigger={isMobile ? "click" : "hover"}
            >
              <button className="action-button  action-button-nft-unlisted">
                {buttonContent}
              </button>{" "}
            </Tooltip>
          )}
        </Row>
      );
    } else if (marketplaceStore.NFT?.supply === 1) {
      return (
        <Row justify="center">
          {marketplaceStore.NFT?.listed &&
          marketplaceStore.metaMaskIsConnected ? (
            <button
              className=" action-button action-button-nft-listed"
              onClick={() => requireAuth(handleSimpleBuyNFT)}
            >
              Buy for {marketplaceStore.NFT?.price}{" "}
              {marketplaceStore.NFT?.chainid === 11155111 ? "ETH" : "MATIC"}
            </button>
          ) : (
            <Tooltip
              title="Buying is currently unavailable. The owner may have disabled it, or your wallet is not connected."
              backgroundColor="#00000000"
              trigger={isMobile ? "click" : "hover"}
            >
              <button className="action-button  action-button-nft-unlisted">
                Buy for {marketplaceStore.NFT?.price}{" "}
                {marketplaceStore.NFT?.chainid === 11155111 ? "ETH" : "MATIC"}
              </button>
            </Tooltip>
          )}
        </Row>
      );
    }

    return (
      <Row justify="center">
        <button
          className="action-button action-button-nft-listed"
          onClick={() =>
            requireAuth(() => {
              if (buttonContent === "MOOC" || buttonContent === "Repository") {
                window.open(marketplaceStore.marketplaceItem?.item.source_media);
              } else if (buttonContent === "Download") {
                handleDownload(
                  marketplaceStore.marketplaceItem.item.source_media,
                );
              }
            })
          }
        >
          {buttonContent}
        </button>
      </Row>
    );
  };

  return renderActionButton();
};

export default observer(ItemDetailsActionButton);
